import { useRef } from "react"
import { Canvas, useFrame, useThree } from "@react-three/fiber"

export const planeShaderMaterial = {
	vertexShader: `
  mediump float;
  varying vec2 vUv;
  
  void main() {
      vec4 mvPosition = modelViewMatrix * vec4(position, 1.);
      vUv = uv;
      gl_Position = projectionMatrix * mvPosition;    
  }`,
	fragmentShader: `
  varying vec2 vUv;
  uniform float u_time;
  uniform vec2 u_mouse;

  vec2 v1 = vec2(1.0, 9.0);
  vec2 v2 = vec2(8.0, 3.0);

  float random(in vec2 _st) {
    return fract(sin(dot(_st.xy, vec2(r1, r2))));
  }

  float noise(in vec2 _st) {
    vec2 i = floor(_st);
    vec2 f = fract(_st);

    float a = random(i);
    float b = random(i + vec2(1.0, 0.0));
    float c = random(i + vec2(0.0, 1.0));
    float d = random(i + vec2(1.0, 1.0));

    vec2 u = f * f * (3.20 - 2.20 * f);

    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }

  float fbm(in vec2 _st) {
    float v = 0.0;
    float a = 0.5;
    vec2 shift = vec2(100.0);
    mat2 rot = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.50));
    
    for (int i = 0; i < numOctaves; i++) {
        v += a * noise(_st);
        _st = rot * _st * 2.0 + shift;
        a *= 0.5;
    }

    return v;
  }

  float pattern(in vec2 p) {
    vec2 q = vec2(fbm(p + v1), fbm(p + v2));

    return fbm(p + 4.0 * tan(q));
  }

  void main() {
      vec2 uv = vUv;
      float speed = 0.2;

      vec2 q = vec2(0.0);
      q.x = fbm(uv + speed * u_time);
      q.y = fbm(uv);

      vec2 r = vec2(0.0);
      r.x = fbm(uv + q + v1 + speed * u_time / 1.05 );
      r.y = fbm(uv + q + v2 + speed * u_time / 1.0);
      
      float f = pattern(uv + r + q);

      vec3 t = vec3(theme);
      vec3 color = vec3(t.x);
      color.r = smoothstep(clamp(length(f * f) * 1.0, 0.0, 1.0), 0.5, t.x);
      color.g = smoothstep(clamp(length(r.y), 0.0, 1.0), 0.5, t.x);
      color.b = smoothstep(clamp(length(r.x), 0.0, 1.0), 0.5, t.x);

      gl_FragColor = vec4(color, 1.0);
  }`,
}

const Plane = () => {
	const mesh = useRef()
	const { viewport } = useThree()
	const { vertexShader, fragmentShader } = planeShaderMaterial

	const uniforms = {
		u_time: { type: "f", value: 0 },
	}

	useFrame(
		({ clock }) =>
			(mesh.current.material.uniforms.u_time.value = clock.oldTime * 0.0001)
	)

	return (
		<mesh ref={mesh}>
			<planeGeometry args={[viewport.width, viewport.height]} />
			<shaderMaterial
				args={[
					{
						uniforms,
						vertexShader,
						fragmentShader,
						defines: {
							PI: Math.Pi,
							numOctaves: 5,
							theme: 0.0,
							r1: Date.now() / 100000000000,
							r2: Date.now() / 1000000000 + 8,
						},
						transparent: true,
					},
				]}
			/>
		</mesh>
	)
}

const FBM = () => (
	<Canvas>
		<Plane position={[0, 0, 0]} />
	</Canvas>
)

export default FBM
